// src/features/executive/insight/widgets/NationalityDonut.tsx
import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import CategoricalDistributionCard from './CategoricalDistributionCard'
import { useNationality } from '../hooks/use-nationality'

// Nationality keys are nationalityIds as strings (or "unknown" for nulls);
// assign colours in enumeration order, wrapping the palette when the long
// tail exceeds palette size.
const PALETTE = ['#2a8d9c', '#e67e22', '#4caf50', '#9b59b6', '#f1c40f', '#95a5a6']

// Trigger a data-quality nudge when "unknown" nationality dominates the
// donut — Thai clinics almost always know whether the patient is Thai or
// foreign, so a large unknown bucket usually means the register form is
// letting staff skip the nationality dropdown.
const UNKNOWN_WARNING_PCT = 15

export default function NationalityDonut() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useNationality()
  const segments = data?.segments ?? []
  const colorMap: Record<string, string> = {}
  segments.forEach((s, idx) => {
    colorMap[s.key] = s.key === 'unknown' ? '#95a5a6' : PALETTE[idx % PALETTE.length]
  })

  const unknown = segments.find((s) => s.key === 'unknown')
  const showUnknownWarning = (unknown?.percent ?? 0) > UNKNOWN_WARNING_PCT

  const notice = showUnknownWarning ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {t(
            'EXECUTIVE.INSIGHT.NATIONALITY.UNSPECIFIED_WARNING',
            'ไม่ระบุสัญชาติ {{pct}}% — กรุณาตรวจสอบให้คนไข้ระบุข้อมูลนี้ในใบลงทะเบียน',
            { pct: (unknown?.percent ?? 0).toFixed(0) }
          )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <CategoricalDistributionCard
      title={t('EXECUTIVE.INSIGHT.NATIONALITY.TITLE')}
      titleTooltip={t('EXECUTIVE.INSIGHT.NATIONALITY.SOURCE_TOOLTIP')}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.NATIONALITY.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      notice={notice}
      segments={segments.map((s) => ({
        ...s,
        label: t(`EXECUTIVE.FILTER.NATIONALITY_OPTIONS.${s.key === 'other' ? 'OTHER' : s.key}`, s.label),
      }))}
      total={data?.total ?? 0}
      previousTotal={data?.previousTotal ?? null}
      colorMap={colorMap}
      centerLabel={t('EXECUTIVE.INSIGHT.TOTAL_PATIENTS')}
      unit={t('EXECUTIVE.INSIGHT.UNIT.PATIENT')}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
